import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import AppBar from "@mui/material/AppBar";
import CssBaseline from "@mui/material/CssBaseline";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import Collapse from "@mui/material/Collapse";
import Badge from "@mui/material/Badge";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import Popover from "@mui/material/Popover";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import {
  ExpandLess,
  ExpandMore,
  LocalOffer as LocalOfferIcon,
  EmojiEvents as EmojiEventsIcon,
  GroupAdd as GroupAddIcon,
  Tune as TuneIcon,
  Redeem as RedeemIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  Receipt as ReceiptIcon,
  Assessment as AssessmentIcon,
  AccountBalance as AccountBalanceIcon,
  AccountBox as AccountBoxIcon,
  Description as DescriptionIcon,
  ListAlt as ListAltIcon,
  // topbar
  NotificationsNoneRounded as BellIcon,
  SettingsOutlined as SettingsIcon,
  LogoutRounded as LogoutIcon,
  KeyboardArrowDownRounded as ChevronIcon,
  DashboardRounded as DashboardIcon,
  BarChartRounded as BarChartIcon,
  Inventory2Rounded as PackageIcon,
  FireTruckRounded as HeavyIcon,
  RouteRounded as TripIcon,
  PersonRounded as DriverIcon,
  LocalShippingRounded as TruckIcon,
  ManageAccountsRounded as OwnerIcon,
  SupportAgentRounded as AgentIcon,
  PeopleRounded as UserIcon,
  MapRounded as MapIcon,
  FmdGoodRounded as ZoneIcon,
  HeadsetMicRounded as ComplaintIcon,
  SmartToyRounded as AiIcon,
  LayersRounded as PricingIcon,
  ShieldRounded as ProtectionIcon,
} from "@mui/icons-material";
import { getData, API_ENDPOINTS } from "../../services/apiService.js";
import "./Drawer.css";

const DRAWER_WIDTH = 232;
const TOPBAR_HEIGHT = 60;

// ── Brand colors ─────────────────────────────────────────────────────────
const C = {
  primary:    "#007C84",
  primaryDk:  "#016268",
  activeBg:   "rgba(0,124,132,0.09)",
  hoverBg:    "rgba(0,124,132,0.05)",
  activeText: "#007C84",
  mutedText:  "#64748B",
  border:     "#E2E8F0",
};

// ── Sidebar nav item ──────────────────────────────────────────────────────
const NavItem = ({ item, isActive, badge, onClick }) => {
  const Icon = item.muiIcon;
  return (
    <ListItem disablePadding sx={{ px: 1.5, mb: 0.25 }}>
      <ListItemButton
        onClick={onClick}
        sx={{
          borderRadius: "10px",
          px: 1.5, py: 0.9,
          bgcolor: isActive ? C.activeBg : "transparent",
          borderLeft: isActive ? `3px solid ${C.primary}` : "3px solid transparent",
          transition: "all 0.15s",
          "&:hover": { bgcolor: isActive ? C.activeBg : C.hoverBg },
        }}
      >
        <ListItemIcon sx={{ minWidth: 32 }}>
          {Icon
            ? <Icon sx={{ fontSize: 18, color: isActive ? C.primary : C.mutedText }} />
            : item.icon && (
              <img
                src={isActive ? item.activeIcon : item.icon}
                alt=""
                style={{
                  width: 18, height: 18,
                  filter: isActive ? "none" : "grayscale(60%) opacity(0.7)",
                }}
              />
            )
          }
        </ListItemIcon>
        <ListItemText
          primary={item.text}
          sx={{
            "& .MuiListItemText-primary": {
              fontSize: 13,
              fontWeight: isActive ? 700 : 500,
              color: isActive ? C.activeText : "#334155",
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              lineHeight: 1.3,
            },
          }}
        />
        {badge > 0 && (
          <Badge badgeContent={badge} color="error" sx={{ mr: 0.5 }} />
        )}
      </ListItemButton>
    </ListItem>
  );
};

// ── Section label ─────────────────────────────────────────────────────────
const SectionLabel = ({ label }) => (
  <Typography
    sx={{
      fontSize: 10, fontWeight: 700, letterSpacing: "0.8px",
      textTransform: "uppercase", color: C.mutedText,
      px: 2.5, pt: 1.8, pb: 0.5,
      fontFamily: "'Plus Jakarta Sans', sans-serif",
    }}
  >
    {label}
  </Typography>
);

// ── Main component ────────────────────────────────────────────────────────
export default function ClippedDrawer() {
  const [dimEnabled, setDimEnabled] = useState(true);
  const [openLogoutDialog, setOpenLogoutDialog] = useState(false);
  const [profilePic, setProfilePic] = useState("/images/Dummy-Image.jpg");
  const [adminName, setAdminName] = useState("Admin");
  const [escalatedCount, setEscalatedCount] = useState(0);
  const [notifAnchor,    setNotifAnchor]    = useState(null);
  const [notifItems,     setNotifItems]     = useState([]);
  const [notifLoading,   setNotifLoading]   = useState(false);
  const [openGroups, setOpenGroups] = useState({});
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    getData("/api/dim/config/public")
      .then(res => { if (!cancelled) setDimEnabled(res?.result?.enabled !== false); })
      .catch(() => { if (!cancelled) setDimEnabled(true); });
    return () => { cancelled = true; };
  }, []);

  // ── Sidebar items ─────────────────────────────────────────────────────
  const sidebarItems = [
    // ── Core
    { _section: "Core" },
    { text: "Dashboard",       path: "dashboard",          muiIcon: DashboardIcon },
    { text: "Pricing Analytics", path: "pricing-analytics", muiIcon: BarChartIcon },

    // ── Operations
    { _section: "Operations" },
    // { text: "Packages", path: "packages", muiIcon: PackageIcon }, // hidden per admin config
    {
      text: "Truck Owners", key: "truck-owners-parent", muiIcon: OwnerIcon,
      children: [
        { text: "All Owners", path: "truck-owners", muiIcon: OwnerIcon },
        { text: "My Team",    path: "agents",       muiIcon: AgentIcon },
      ],
    },
    { text: "Trips",          path: "trips",                muiIcon: TripIcon },
    { text: "Trucks",         path: "trucks",               muiIcon: TruckIcon },
    { text: "Drivers",        path: "drivers",              muiIcon: DriverIcon },
    { text: "Heavy Cargo",    path: "heavy-cargo-enquiries",muiIcon: HeavyIcon },
    { text: "Customers",      path: "users",                muiIcon: UserIcon },
    { text: "Delivery Zones", path: "delivery-zones",       muiIcon: ZoneIcon },
    { text: "Fleet Map",      path: "fleet-map",            muiIcon: MapIcon },

    // ── Finance
    { _section: "Finance" },
    {
      text: "Payments & Payouts", key: "payments-parent", muiIcon: AccountBalanceWalletIcon,
      children: [
        { text: "Transactions",   path: "payments",               muiIcon: ReceiptIcon,     matchPaths: ["payments"], exactMatch: true },
        { text: "Reconciliation", path: "payments/reconciliation", muiIcon: AssessmentIcon },
        { text: "Withdrawals",    path: "payments/withdrawals",    muiIcon: AccountBalanceIcon },
        { text: "Bank Accounts",  path: "payments/bank-accounts",  muiIcon: AccountBoxIcon },
      ],
    },

    // ── Compliance
    { _section: "Compliance" },
    { text: "Complaints",      path: "complaints",          muiIcon: ComplaintIcon, showBadge: true },
    { text: "Cargo Protection", path: "protection-claims",  muiIcon: ProtectionIcon },
    {
      text: "E-Way Bills", key: "ewb-parent", muiIcon: DescriptionIcon,
      children: [
        { text: "Compliance Dashboard", path: "ewb-compliance", muiIcon: AssessmentIcon },
        { text: "Bookings",             path: "ewb-bookings",   muiIcon: ListAltIcon },
        { text: "Configuration",        path: "ewb-config",     muiIcon: TuneIcon },
      ],
    },

    // ── Platform
    { _section: "Platform" },
    { text: "AI Insights Ops",  path: "ai-insights-ops",    muiIcon: AiIcon },
    { text: "Pricing Tiers",    path: "pricing-tiers",       muiIcon: PricingIcon },
    {
      text: "Discounts & Incentives", key: "dim-parent", muiIcon: RedeemIcon,
      children: [
        { text: "Campaigns",    path: "campaigns",    muiIcon: LocalOfferIcon, matchPaths: ["campaigns", "campaigns/new", "campaigns/"] },
        { text: "Loyalty Tiers", path: "loyalty-admin", muiIcon: EmojiEventsIcon },
        { text: "Referrals",    path: "referral-queue", muiIcon: GroupAddIcon },
        { text: "Configuration", path: "dim-config",   muiIcon: TuneIcon },
      ].filter(c => dimEnabled || c.path === "dim-config"),
    },

    // ── Admin
    { _section: "Admin" },
    { text: "Settings", path: "settings", muiIcon: SettingsIcon },
  ];

  const currentRoute = (location.pathname || "").replace(/^\/app\/?/, "");

  // Auto-open parent group when a child is active
  useEffect(() => {
    const next = { ...openGroups };
    let changed = false;
    for (const item of sidebarItems) {
      if (!item.children) continue;
      const childActive = item.children.some(c =>
        currentRoute === c.path ||
        currentRoute.startsWith(c.path + "/") ||
        (c.matchPaths || []).some(p => currentRoute === p || currentRoute.startsWith(p))
      );
      if (childActive && !next[item.key]) { next[item.key] = true; changed = true; }
    }
    if (changed) setOpenGroups(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const toggleGroup = key => setOpenGroups(prev => ({ ...prev, [key]: !prev[key] }));

  useEffect(() => {
    const fetchAdminData = async () => {
      const raw = localStorage.getItem("userData");
      if (!raw) return;
      const userData = JSON.parse(raw);
      const adminId = userData?.admin_id;
      if (!adminId) return;
      try {
        const res = await getData(API_ENDPOINTS.getAdminById, { id: adminId });
        if (res?.result?.pic) setProfilePic(res.result.pic);
        if (res?.result?.first_name) {
          setAdminName(`${res.result.first_name} ${res.result.last_name || ""}`.trim());
        }
      } catch (_) {}
    };
    const fetchEscalatedCount = async () => {
      try {
        const res = await getData(API_ENDPOINTS.getComplaintStats);
        if (res?.result) {
          const { escalated, sla_breaches } = res.result;
          setEscalatedCount((escalated || 0) + (sla_breaches?.total_breached || 0));
        }
      } catch (_) {}
    };
    fetchAdminData();
    fetchEscalatedCount();
  }, []);

  const handleBellClick = async (e) => {
    setNotifAnchor(e.currentTarget);
    if (notifItems.length > 0) return; // already loaded
    setNotifLoading(true);
    try {
      const res = await getData(API_ENDPOINTS.getAllComplaints, { status:"escalated", limit:8, page:1 });
      setNotifItems(res?.result?.rows || res?.result?.complaints || []);
    } catch(_) { setNotifItems([]); }
    finally { setNotifLoading(false); }
  };

  const handleBellClose = () => {
    setNotifAnchor(null);
    // User has seen the notifications — clear the badge until next page load
    if (notifItems.length > 0 || !notifLoading) setEscalatedCount(0);
  };

  const isChildActive = (c) => c.exactMatch
    ? currentRoute === c.path || (c.matchPaths || []).includes(currentRoute)
    : currentRoute === c.path || currentRoute.startsWith(c.path + "/") ||
      (c.matchPaths || []).some(p => currentRoute === p || currentRoute.startsWith(p));

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />

      {/* ── Top AppBar ── */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: t => t.zIndex.drawer + 1,
          background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDk} 100%)`,
          height: TOPBAR_HEIGHT,
          boxShadow: "0 2px 12px rgba(0,124,132,0.25)",
        }}
      >
        <Toolbar className="navbar" sx={{ minHeight: `${TOPBAR_HEIGHT}px !important` }}>
          {/* Logo */}
          <Box sx={{
            display: "inline-flex", alignItems: "center",
            bgcolor: "rgba(255,255,255,0.15)",
            border: "1px solid rgba(255,255,255,0.25)",
            borderRadius: "12px", px: 1.2, py: 0.6,
          }}>
            <img src="/images/sut-icon.png" alt="SUT" style={{ height: 30, width: "auto" }} />
          </Box>

          {/* Right actions */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {/* Settings */}
            <Tooltip title="Settings">
              <NavLink to="settings" className="nav-icon-btn">
                <SettingsIcon sx={{ fontSize: 18 }} />
              </NavLink>
            </Tooltip>

            {/* Bell */}
            <Tooltip title="Notifications">
              <IconButton onClick={handleBellClick} size="small"
                sx={{ bgcolor:"rgba(255,255,255,0.15)", border:"1px solid rgba(255,255,255,0.2)",
                  borderRadius:"10px", width:36, height:36, color:"#fff",
                  "&:hover":{ bgcolor:"rgba(255,255,255,0.25)" } }}>
                <Badge
                  badgeContent={escalatedCount > 0 ? escalatedCount : null}
                  color="error"
                  sx={{ "& .MuiBadge-badge":{ fontSize:10, minWidth:16, height:16, top:-2, right:-2 } }}
                >
                  <BellIcon sx={{ fontSize:18 }} />
                </Badge>
              </IconButton>
            </Tooltip>

            {/* Notification popover */}
            <Popover
              open={Boolean(notifAnchor)} anchorEl={notifAnchor} onClose={handleBellClose}
              anchorOrigin={{ vertical:"bottom", horizontal:"right" }}
              transformOrigin={{ vertical:"top", horizontal:"right" }}
              PaperProps={{ sx:{
                borderRadius:"16px", width:340, maxHeight:480,
                boxShadow:"0 8px 32px rgba(0,0,0,0.14)",
                border:"1px solid #E2E8F0", overflow:"hidden",
              }}}
            >
              {/* Header */}
              <Box sx={{ px:2, py:1.5, background:"linear-gradient(135deg,#007C84,#016268)",
                display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                <Box sx={{ display:"flex", alignItems:"center", gap:1 }}>
                  <BellIcon sx={{ fontSize:16, color:"#fff" }} />
                  <Typography sx={{ fontSize:13, fontWeight:800, color:"#fff", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
                    Notifications
                  </Typography>
                </Box>
                {escalatedCount > 0 && (
                  <Chip size="small" label={`${escalatedCount} escalated`}
                    sx={{ bgcolor:"rgba(255,255,255,0.2)", color:"#fff", fontWeight:700, fontSize:10, height:20,
                      border:"1px solid rgba(255,255,255,0.3)" }} />
                )}
              </Box>

              {/* Body */}
              <Box sx={{ overflow:"auto", maxHeight:360 }}>
                {notifLoading ? (
                  <Box sx={{ py:4, textAlign:"center" }}>
                    <CircularProgress size={24} sx={{ color:"#007C84" }} />
                    <Typography sx={{ fontSize:12, color:"#64748B", mt:1 }}>Loading notifications…</Typography>
                  </Box>
                ) : notifItems.length === 0 ? (
                  <Box sx={{ py:4, textAlign:"center" }}>
                    <BellIcon sx={{ fontSize:32, color:"#E2E8F0", mb:1 }} />
                    <Typography sx={{ fontSize:13, color:"#64748B" }}>No escalated complaints</Typography>
                  </Box>
                ) : (
                  notifItems.map((c, i) => {
                    const id = c.complaint_id || c.id;
                    const title = c.complaint_type || c.type || "Complaint";
                    const when = c.createdAt ? new Date(c.createdAt).toLocaleDateString("en-IN") : "";
                    const pkg = c.tracking_code || c.Package?.tracking_code || "";
                    return (
                      <Box key={id || i}
                        onClick={()=>{ setEscalatedCount(0); navigate(`/app/complaints/${id}`); handleBellClose(); }}
                        sx={{ px:2, py:1.5, cursor:"pointer", borderBottom:"1px solid #F1F5F9",
                          "&:hover":{ bgcolor:"#F8FAFC" }, "&:last-child":{ borderBottom:"none" } }}>
                        <Box sx={{ display:"flex", alignItems:"center", justifyContent:"space-between", mb:0.3 }}>
                          <Typography sx={{ fontSize:13, fontWeight:700, color:"#0F172A",
                            textTransform:"capitalize", lineHeight:1.3 }}>
                            {title.replace(/_/g," ")}
                          </Typography>
                          <Box sx={{ px:0.8, py:0.2, bgcolor:"#FFE4E6", borderRadius:"6px" }}>
                            <Typography sx={{ fontSize:10, fontWeight:700, color:"#E11D48" }}>Escalated</Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display:"flex", alignItems:"center", gap:1 }}>
                          {pkg && (
                            <Typography sx={{ fontFamily:"monospace", fontSize:11, color:"#007C84",
                              bgcolor:"#E0F4F5", px:0.7, py:0.1, borderRadius:"5px" }}>
                              {pkg}
                            </Typography>
                          )}
                          <Typography sx={{ fontSize:11, color:"#64748B" }}>
                            #{id} · {when}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })
                )}
              </Box>

              {/* Footer */}
              <Box sx={{ px:2, py:1.2, borderTop:"1px solid #E2E8F0", bgcolor:"#F8FAFC",
                display:"flex", justifyContent:"center" }}>
                <Box onClick={()=>{ navigate("/app/complaints"); handleBellClose(); }}
                  sx={{ fontSize:12, fontWeight:700, color:"#007C84", cursor:"pointer",
                    "&:hover":{ textDecoration:"underline" } }}>
                  View all complaints →
                </Box>
              </Box>
            </Popover>

            {/* Admin pill */}
            <NavLink to="profile" className="admin-pill" style={{ textDecoration: "none" }}>
              <img src={profilePic} alt="Admin" className="admin-avatar" />
              <span className="admin-name">{adminName}</span>
              <ChevronIcon sx={{ fontSize: 16, color: "rgba(255,255,255,0.7)", ml: -0.5 }} />
            </NavLink>
          </Box>
        </Toolbar>
      </AppBar>

      {/* ── Sidebar ── */}
      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: DRAWER_WIDTH,
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            bgcolor: "#FFFFFF",
            borderRight: `1px solid ${C.border}`,
            pt: `${TOPBAR_HEIGHT}px`,
          },
        }}
      >
        <Box sx={{ overflow: "auto", flexGrow: 1, pb: 1, pt: 1 }}>
          <List disablePadding>
            {sidebarItems.map((item, idx) => {

              // ── Section label
              if (item._section) {
                return (
                  <React.Fragment key={`section-${item._section}`}>
                    {idx > 0 && <Divider sx={{ mx: 2, my: 0.5, borderColor: C.border }} />}
                    <SectionLabel label={item._section} />
                  </React.Fragment>
                );
              }

              // ── Group (with children)
              if (item.children?.length > 0) {
                const isOpen = !!openGroups[item.key];
                const ParentIcon = item.muiIcon;
                const anyChildActive = item.children.some(c => isChildActive(c));

                return (
                  <React.Fragment key={item.key}>
                    <ListItem disablePadding sx={{ px: 1.5, mb: 0.25 }}>
                      <ListItemButton
                        onClick={() => toggleGroup(item.key)}
                        sx={{
                          borderRadius: "10px",
                          px: 1.5, py: 0.9,
                          bgcolor: anyChildActive ? C.activeBg : "transparent",
                          borderLeft: anyChildActive ? `3px solid ${C.primary}` : "3px solid transparent",
                          transition: "all 0.15s",
                          "&:hover": { bgcolor: anyChildActive ? C.activeBg : C.hoverBg },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          {ParentIcon && (
                            <ParentIcon sx={{ fontSize: 18, color: anyChildActive ? C.primary : C.mutedText }} />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.text}
                          sx={{
                            "& .MuiListItemText-primary": {
                              fontSize: 13, fontWeight: anyChildActive ? 700 : 500,
                              color: anyChildActive ? C.activeText : "#334155",
                              fontFamily: "'Plus Jakarta Sans', sans-serif",
                            },
                          }}
                        />
                        {isOpen
                          ? <ExpandLess sx={{ fontSize: 16, color: C.mutedText }} />
                          : <ExpandMore sx={{ fontSize: 16, color: C.mutedText }} />
                        }
                      </ListItemButton>
                    </ListItem>

                    <Collapse in={isOpen} timeout="auto" unmountOnExit>
                      <List disablePadding sx={{ pl: 1.5, pr: 1.5, mb: 0.5 }}>
                        {item.children.map(c => {
                          const ChildIcon = c.muiIcon;
                          const active = isChildActive(c);
                          return (
                            <NavLink key={c.path} to={c.path} style={{ textDecoration: "none" }}>
                              <ListItemButton
                                sx={{
                                  borderRadius: "8px",
                                  pl: 3.5, pr: 1.5, py: 0.75,
                                  bgcolor: active ? C.activeBg : "transparent",
                                  "&:hover": { bgcolor: active ? C.activeBg : C.hoverBg },
                                  mb: 0.2,
                                }}
                              >
                                <ListItemIcon sx={{ minWidth: 26 }}>
                                  {ChildIcon && (
                                    <ChildIcon sx={{ fontSize: 15, color: active ? C.primary : "#94A3B8" }} />
                                  )}
                                </ListItemIcon>
                                <ListItemText
                                  primary={c.text}
                                  sx={{
                                    "& .MuiListItemText-primary": {
                                      fontSize: 12.5, fontWeight: active ? 600 : 400,
                                      color: active ? C.activeText : "#475569",
                                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                                    },
                                  }}
                                />
                              </ListItemButton>
                            </NavLink>
                          );
                        })}
                      </List>
                    </Collapse>
                  </React.Fragment>
                );
              }

              // ── Leaf item
              const isActive = currentRoute === item.path ||
                currentRoute.startsWith(item.path + "/");
              return (
                <NavLink key={item.path} to={item.path} style={{ textDecoration: "none" }}>
                  <NavItem
                    item={item}
                    isActive={isActive}
                    badge={item.showBadge ? escalatedCount : 0}
                  />
                </NavLink>
              );
            })}
          </List>
        </Box>

        {/* ── Logout ── */}
        <Box sx={{ borderTop: `1px solid ${C.border}`, px: 1.5, py: 1.2 }}>
          <ListItemButton
            onClick={() => setOpenLogoutDialog(true)}
            sx={{
              borderRadius: "10px", px: 1.5, py: 0.9,
              "&:hover": { bgcolor: "rgba(239,68,68,0.07)" },
            }}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              <LogoutIcon sx={{ fontSize: 18, color: "#EF4444" }} />
            </ListItemIcon>
            <ListItemText
              primary="Logout"
              sx={{
                "& .MuiListItemText-primary": {
                  fontSize: 13, fontWeight: 500, color: "#EF4444",
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                },
              }}
            />
          </ListItemButton>
        </Box>
      </Drawer>

      {/* ── Main content ── */}
      <Box component="main" sx={{ flexGrow: 1, p: 3, bgcolor: "background.default", minHeight: "100vh" }}>
        <Box sx={{ height: `${TOPBAR_HEIGHT}px` }} />
        <Outlet />
      </Box>

      {/* ── Logout dialog ── */}
      <Dialog open={openLogoutDialog} onClose={() => setOpenLogoutDialog(false)}
        PaperProps={{ sx:{ borderRadius:"20px", overflow:"hidden", maxWidth:360, width:"100%" } }}>
        {/* Rose accent stripe */}
        <Box sx={{ height:4, background:"linear-gradient(90deg,#E11D48,#FFE4E6)" }} />

        {/* Icon + title */}
        <Box sx={{ pt:3, px:3, pb:0, display:"flex", flexDirection:"column", alignItems:"center", gap:1.5 }}>
          <Box sx={{ width:52, height:52, borderRadius:"16px", bgcolor:"#FFE4E6",
            display:"flex", alignItems:"center", justifyContent:"center" }}>
            <LogoutIcon sx={{ fontSize:24, color:"#E11D48" }} />
          </Box>
          <Box sx={{ textAlign:"center" }}>
            <Typography sx={{ fontSize:17, fontWeight:800, color:"#0F172A",
              fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
              Confirm Logout
            </Typography>
            <Typography sx={{ fontSize:13, color:"#64748B", mt:0.5, lineHeight:1.5 }}>
              You'll be signed out of the SYT Admin panel.<br />
              Any unsaved changes will be lost.
            </Typography>
          </Box>
        </Box>

        {/* Admin info pill */}
        <Box sx={{ mx:3, mt:2, px:2, py:1.2, bgcolor:"#F8FAFC",
          borderRadius:"12px", border:"1px solid #E2E8F0",
          display:"flex", alignItems:"center", gap:1.5 }}>
          <Box sx={{ width:32, height:32, borderRadius:"50%", bgcolor:"#E0F4F5",
            display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
            <Typography sx={{ fontSize:13, fontWeight:800, color:"#007C84" }}>
              {adminName?.charAt(0)?.toUpperCase() || "A"}
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize:12, fontWeight:700, color:"#0F172A" }}>{adminName}</Typography>
            <Typography sx={{ fontSize:11, color:"#64748B" }}>Administrator</Typography>
          </Box>
        </Box>

        <DialogActions sx={{ px:3, py:2.5, gap:1, mt:1 }}>
          <Button onClick={() => setOpenLogoutDialog(false)} variant="outlined" fullWidth
            sx={{ borderRadius:"12px", textTransform:"none", fontWeight:700,
              borderColor:"#E2E8F0", color:"#64748B", py:1,
              "&:hover":{ bgcolor:"#F8FAFC", borderColor:"#CBD5E1" } }}>
            Cancel
          </Button>
          <Button fullWidth variant="contained" disableElevation
            onClick={() => {
              localStorage.removeItem("userData");
              localStorage.removeItem("authToken");
              setOpenLogoutDialog(false);
              navigate("/");
            }}
            sx={{ borderRadius:"12px", textTransform:"none", fontWeight:700, py:1,
              bgcolor:"#E11D48", "&:hover":{ bgcolor:"#be123c" } }}>
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
